import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PrintIcon from '@mui/icons-material/Print'
import { Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { startTransition, useActionState, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useUserFullName } from '../user/hooks/user.hooks'
import SurveyRenderer from './components/SurveyRenderer'
import { useQualityTest, useSubmitTestExecution } from './services/QualityControlHooks'

const TestExecutionPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { testId } = useParams<{ testId: string }>()
  const userName = useUserFullName() ?? ''
  const locationState = location.state as {
    initialData?: Record<string, unknown>
    correctionOfId?: string
    correctionVersionId?: string
  } | null

  const versionIdToLoad = locationState?.correctionVersionId ?? testId ?? ''
  const { qualityTest } = useQualityTest(versionIdToLoad)
  const submitMutation = useSubmitTestExecution()

  const lastDataRef = useRef<Record<string, unknown> | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const [errorMsg, submitAction, isPending] = useActionState(
    async (_prevState: string | null, data: Record<string, unknown>): Promise<string | null> => {
      try {
        const nameKey = Object.keys(data).find((k) => k.toLowerCase() === 'name')
        const observedByValue = nameKey && typeof data[nameKey] === 'string' && data[nameKey] ? data[nameKey] : userName

        await submitMutation.mutateAsync({
          testVersionId: qualityTest.id,
          testGroupId: testId ?? '',
          submission: {
            executionDate: new Date().toISOString(),
            observedBy: observedByValue,
            formResult: JSON.stringify(data),
            correctionOfId: locationState?.correctionOfId,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        })
        setIsSubmitted(true)
        startTransition(() => navigate('/quality-control'))
        return null
      } catch {
        return t('qualityControl.submitError')
      }
    },
    null,
  )

  if (!testId) {
    navigate('/quality-control')
    return null
  }

  const handleFormComplete = (data: Record<string, unknown>) => {
    lastDataRef.current = data
    startTransition(() => submitAction(data))
  }

  const formAreaContent = isPending ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, gap: 2 }}>
      <CircularProgress color="secondary" />
      <Typography>{t('qualityControl.submitting')}</Typography>
    </Box>
  ) : isSubmitted ? (
    <Alert severity="success" sx={{ mt: 2 }}>
      {t('qualityControl.submitSuccess')}
    </Alert>
  ) : errorMsg ? (
    <Box sx={{ mt: 2 }}>
      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
        {errorMsg}
      </Alert>
      <Button variant="contained" onClick={() => lastDataRef.current && submitAction(lastDataRef.current)}>
        {t('qualityControl.retry')}
      </Button>
    </Box>
  ) : (
    <SurveyRenderer
      formJson={qualityTest.form}
      mode="edit"
      onComplete={handleFormComplete}
      initialData={locationState?.initialData}
    />
  )

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, '@media print': { display: 'none' } }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/quality-control')}
        >
          {t('qualityControl.backToList')}
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ ml: 'auto' }}
        >
          {t('qualityControl.print')}
        </Button>
      </Box>

      {/* Metadata header — flat Paper, two columns on desktop */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="flex-start">
          {/* Left: name + description */}
          <Grid size={{ xs: 12, sm: 8 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {qualityTest.displayName}
            </Typography>
            {qualityTest.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {qualityTest.description}
              </Typography>
            )}
          </Grid>

          {/* Right: category chip, date chip, observer */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'flex-start', sm: 'flex-end' },
                gap: 1,
              }}
            >
              {qualityTest.category && (
                <Chip
                  label={qualityTest.category}
                  size="small"
                  sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
                />
              )}
              <Chip
                label={new Date(qualityTest.lastUpdatedAt).toLocaleDateString()}
                size="small"
                variant="outlined"
                icon={<CalendarTodayIcon />}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonOutlineIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {userName}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Form container — yellow top border accent, no redundant heading */}
      <Box
        sx={{
          borderTop: '4px solid',
          borderColor: 'primary.main',
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
          p: 2,
        }}
      >
        {formAreaContent}
      </Box>
    </Box>
  )
}

export default TestExecutionPage
